import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "setup";
import { CPLCalendarEvent, NewCPLCalendarEvent } from "services/classes/CPLCalendarEvent";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { ICalendarEventService } from "services/CalendarEventService";
import { IWorkingHoursService } from "services/WorkingHoursService";
import { OverrideWorkingHoursModel, WorkingHoursModel } from "services/api/cplapi/CPLAPI";

const initialState = {
  Events: new Array<CPLCalendarEvent>(),
  StandardWorkingHours: new Array<WorkingHoursModel>(),
  OverrideWorkingHours: new Array<OverrideWorkingHoursModel>(),
  Loading: false,
  NewEventID: 0,
};

export const cplCalendarSlice = createSlice({
  name: "cplCalendarSlice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllEventsForUser.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getAllEventsForUser.fulfilled, (state, action) => {
        state.Events = action.payload;
        state.Loading = false;
      })
      .addCase(getAllEventsForUser.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(createNewEvent.pending, (state) => {
        state.Loading = true;
      })
      .addCase(createNewEvent.fulfilled, (state, action) => {
        state.NewEventID = action.payload;
        state.Loading = false;
      })
      .addCase(createNewEvent.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(editEvent.pending, (state) => {
        state.Loading = true;
      })
      .addCase(editEvent.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(editEvent.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(removeEvent.pending, (state) => {
        state.Loading = true;
      })
      .addCase(removeEvent.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(removeEvent.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(getStandardWorkingHours.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getStandardWorkingHours.fulfilled, (state, action) => {
        state.StandardWorkingHours = action.payload;
        state.Loading = false;
      })
      .addCase(getStandardWorkingHours.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(getOverrideWorkingHours.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getOverrideWorkingHours.fulfilled, (state, action) => {
        state.OverrideWorkingHours = action.payload;
        state.Loading = false;
      })
      .addCase(getOverrideWorkingHours.rejected, (state) => {
        state.Loading = false;
      });
  },
});

export const getAllEventsForUser = createAsyncThunk("cplCalendar/GetAllEventsForUser", async () => {
  const calendarEventService = container.get<ICalendarEventService>(SERVICE_TYPES.CalendarEventService);
  return await calendarEventService.getAllEventsForUser();
});

export const createNewEvent = createAsyncThunk<number, { event: NewCPLCalendarEvent }, { state: RootState }>(
  "cplCalendar/CreateNewEvent",
  async ({ event }, thunkAPI) => {
    const calendarEventService = container.get<ICalendarEventService>(SERVICE_TYPES.CalendarEventService);
    const eventID = await calendarEventService.createCalendarEvent(event);
    thunkAPI.dispatch(assignUserToEvent({ eventID }));
    return eventID;
  }
);

export const assignUserToEvent = createAsyncThunk<void, { eventID: number }, { state: RootState }>(
  "cplCalendar/AssignUserToEvent",
  async ({ eventID }, thunkAPI) => {
    const state = thunkAPI.getState();
    const calendarEventService = container.get<ICalendarEventService>(SERVICE_TYPES.CalendarEventService);
    await calendarEventService.createCalendarEventUserMapping(eventID, state.auth.user!.rpmUserID!);
    thunkAPI.dispatch(getAllEventsForUser());
  }
);

export const editEvent = createAsyncThunk<void, { event: CPLCalendarEvent }, { state: RootState }>(
  "cplCalendar/EditEvent",
  async ({ event }, thunkAPI) => {
    const calendarEventService = container.get<ICalendarEventService>(SERVICE_TYPES.CalendarEventService);
    await calendarEventService.updateCalendarEvent(event);
    thunkAPI.dispatch(getAllEventsForUser());
  }
);

export const removeEvent = createAsyncThunk<void, { eventID: number }>(
  "cplCalendar/RemoveEvent",
  async ({ eventID }, thunkAPI) => {
    const calendarEventService = container.get<ICalendarEventService>(SERVICE_TYPES.CalendarEventService);
    await calendarEventService.deleteCalendarEvent(eventID);
    thunkAPI.dispatch(getAllEventsForUser());
  }
);

export const getStandardWorkingHours = createAsyncThunk("workWeekView/GetStandardWorkingHours", async () => {
  const workingHoursService = container.get<IWorkingHoursService>(SERVICE_TYPES.WorkingHoursService);
  return await workingHoursService.getStandardWorkingHours();
});

export const getOverrideWorkingHours = createAsyncThunk("workWeekView/GetOverrideWorkingHours", async () => {
  const workingHoursService = container.get<IWorkingHoursService>(SERVICE_TYPES.WorkingHoursService);
  return await workingHoursService.getOverrideWorkingHours();
});

export const selectEvents = (state: RootState) => {
  return state.cplCalendar.Events;
};
export const selectIsLoading = (state: RootState) => {
  return state.cplCalendar.Loading;
};
export const selectNewEventID = (state: RootState) => {
  return state.cplCalendar.NewEventID;
};
export const selectStandardWorkingHours = (state: RootState) => {
  return state.cplCalendar.StandardWorkingHours;
};
export const selectOverrideWorkingHours = (state: RootState) => {
  return state.cplCalendar.OverrideWorkingHours;
};

export default cplCalendarSlice.reducer;
